/**
 * frontend/src/pages/valuation/_useAutosave.ts
 *
 * VAL-SAVE-ON-TYPE-1A — debounced save-on-change. `save` resolves null on
 * success or the failure message. Latest wins; the payload present when
 * `enabled` turns true is the baseline and is never saved back; a pending
 * change is flushed on unmount.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'blocked'

const savedLabel = () => `Saved · ${new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`

export function useAutosave<P>(
  payload: P,
  save: (p: P) => Promise<string | null>,
  opts: { enabled: boolean; delayMs?: number; blocked?: string | null; dirty?: boolean },
): { status: AutosaveStatus; message: string | null } {
  const { enabled, delayMs = 1500, blocked = null, dirty = false } = opts
  const [state, setState] = useState<{ status: AutosaveStatus; message: string | null }>({ status: 'idle', message: null })
  const key = JSON.stringify(payload)
  const latest = useRef({ payload, save, blocked })
  latest.current = { payload, save, blocked }
  const savedKey = useRef<string | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inFlight = useRef(false)
  const alive = useRef(true)

  const flush = useCallback(async (nowKey: string) => {
    timer.current = null
    if (latest.current.blocked) {
      if (alive.current) setState({ status: 'blocked', message: latest.current.blocked })
      return
    }
    if (inFlight.current) return // the running save re-checks when it ends
    inFlight.current = true
    savedKey.current = nowKey
    if (alive.current) setState({ status: 'saving', message: null })
    let err: string | null
    try { err = await latest.current.save(latest.current.payload) } catch { err = 'Could not save.' }
    inFlight.current = false
    if (alive.current) setState(err ? { status: 'error', message: err } : { status: 'saved', message: savedLabel() })
    const next = JSON.stringify(latest.current.payload)
    if (alive.current && next !== nowKey && timer.current === null) void flush(next)
  }, [])

  // `dirty`: the payload at enable time already holds edits the server has not
  // seen (typed before a late draft arrived), so it is not a baseline to skip.
  useEffect(() => { if (enabled && !dirty) savedKey.current = key }, [enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || key === savedKey.current) return
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { void flush(key) }, delayMs)
  }, [key, enabled, delayMs, blocked, flush])

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
      if (timer.current) {
        clearTimeout(timer.current)
        timer.current = null
        if (!latest.current.blocked) {
          const { save: doSave, payload: last } = latest.current
          const report = (e: unknown) => console.error('Autosave on unmount failed:', e instanceof Error ? e.message : String(e))
          try { void doSave(last).catch(report) } catch (e) { report(e) }
        }
      }
    }
  }, [])

  return state
}
